'use client';

import { isPathMatch } from '@/@studio/utils';
import { useEffectiveOrgUiContext } from '@/@subsidiary/hooks/useEffectiveOrgUiContext';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { AccessReason, opensInternalHref, resolveNavChild } from '../access/resolve-access';
import { NAV_SECTIONS, NavChild, NavSection } from '../constants';
import { useAccessContext } from './use-access-context';

export interface ResolvedNavChild extends NavChild {
    isSelected: boolean;
    isDisabled: boolean;
    /** Why the row is disabled — drives the hover copy so a license-blocked
     * row does not claim to be "Coming soon". */
    disabledReason?: AccessReason;
    /** `internalTooltip`, but only for internal (@mfour.com) viewers —
     * `undefined` for everyone else so the row falls back to its default
     * behavior (no tooltip, or "Coming soon" for an inert row). */
    tooltip?: string;
}

export interface ResolvedNavSection extends Omit<NavSection, 'children'> {
    children: ResolvedNavChild[];
    isSelected: boolean;
}

/**
 * Resolves `NAV_SECTIONS` against the current route and the shared access
 * rules in `@nav/access/resolve-access`.
 *
 * The four governors used to live inline here. They were lifted out so the
 * Home screen's stage tiles could apply exactly the same decisions — the two
 * surfaces had drifted far enough apart that external users saw different
 * things on each. `nav-access-characterization.test.ts` pins this hook's
 * behavior to what it was before that extraction.
 *
 * The nav DROPS rows the org has no entitlement for; Home renders the same
 * items disabled instead. That difference is presentation only — the decision
 * itself comes from one resolver.
 */
export function useNavSections(): ResolvedNavSection[] {
    const pathname = usePathname();
    const effectiveOrg = useEffectiveOrgUiContext();
    const ctx = useAccessContext();

    return useMemo(() => {
        return NAV_SECTIONS.map((section) => {
            const children: ResolvedNavChild[] = section.children
                .map((child) => ({ child, verdict: resolveNavChild(child, ctx) }))
                .filter(({ verdict }) => !verdict.isHidden)
                .map(({ child, verdict }) => {
                    const flyout = verdict.flyout;

                    return {
                        ...child,
                        href: verdict.href,
                        // `internalHref` rows read as "coming soon" to a
                        // non-internal viewer and as a real destination to an
                        // internal one, so this cannot just pass `child`'s own
                        // value through — `NavChildRow` folds it into `inert`.
                        // Staff outside an `internalRequiresFlag` alpha read it
                        // as coming soon too.
                        comingSoon: child.internalHref
                            ? !opensInternalHref(child, ctx)
                            : child.comingSoon,
                        flyout,
                        tooltip: effectiveOrg.isInternalForTiles
                            ? child.internalTooltip
                            : undefined,
                        isSelected:
                            (!!verdict.href && isPathMatch(verdict.href, pathname)) ||
                            !!flyout?.some((item) => isPathMatch(item.href, pathname)),
                        isDisabled: verdict.isDisabled,
                        disabledReason: verdict.reason,
                    };
                })
                // A flyout-only row with every child flagged off has
                // nothing to render — drop it the same way a
                // `requiresFlag` parent is dropped above.
                .filter(
                    (child) =>
                        !child.flyout ||
                        child.flyout.length > 0 ||
                        !!child.href ||
                        !!child.comingSoon ||
                        !!child.opensSynthesizeModal,
                );

            return {
                ...section,
                children,
                isSelected: children.some((c) => c.isSelected),
            };
        }).filter((section) => section.children.length > 0);
    }, [pathname, effectiveOrg, ctx]);
}
